import { useAuth } from '@/lib/auth/AuthContext';
import { Button } from '@/ui/button';

interface SignInPromptProps {
    error: Error;
}

interface FriendlyError {
    headline: string;
    body: string;
}

/**
 * Maps OIDC error messages we routinely see to a plain-English explanation a
 * partner-bank user can act on. Falls back to a generic message when the cause
 * is unfamiliar — the raw technical message is always shown underneath for
 * debugging.
 */
function explain(error: Error): FriendlyError {
    const raw = error.message.toLowerCase();

    if (raw.includes('session not active') || raw.includes('session_not_active')) {
        return {
            headline: 'Your sign-in session has expired',
            body: 'For your security, the Console signed you out after a period of inactivity. Sign in again to continue.',
        };
    }
    if (raw.includes('invalid_grant') || raw.includes('refresh')) {
        return {
            headline: 'Your sign-in could not be renewed',
            body: 'The Console tried to refresh your session in the background and could not. Sign in again to continue.',
        };
    }
    if (raw.includes('invalid_scope')) {
        return {
            headline: 'Sign-in rejected by the identity provider',
            body: 'The Console requested permissions that are not configured for your account. Contact partnerships@safarapi.com — this is a configuration issue, not something you can fix yourself.',
        };
    }
    if (raw.includes('network') || raw.includes('fetch')) {
        return {
            headline: 'Could not reach the sign-in service',
            body: 'Check your network connection and try again. If the problem persists, the Keycloak service may be down.',
        };
    }
    return {
        headline: 'We could not complete the previous sign-in attempt',
        body: 'Please retry. If the problem persists, contact partnerships@safarapi.com with the technical detail below.',
    };
}

/**
 * Displayed when the OIDC flow returns an error (e.g., realm down, callback misconfigured).
 * Keeps the user in control rather than looping into a broken redirect.
 */
export function SignInPrompt({ error }: SignInPromptProps) {
    const { signIn } = useAuth();
    const friendly = explain(error);

    return (
        <div className="flex min-h-screen items-center justify-center bg-background px-6">
            <div className="w-full max-w-md space-y-4 rounded-lg border border-border bg-card p-8">
                <div className="space-y-1">
                    <p className="font-mono text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                        SafarAPI Console
                    </p>
                    <h1 className="font-display text-xl font-bold">{friendly.headline}</h1>
                </div>
                <p className="text-sm text-muted-foreground">{friendly.body}</p>
                <Button variant="primary" onClick={signIn} className="w-full" autoFocus>
                    Sign in with SSO
                </Button>
                <details className="group">
                    <summary className="cursor-pointer select-none font-mono text-2xs uppercase tracking-wider text-muted-foreground hover:text-foreground">
                        Technical detail
                    </summary>
                    <p className="mt-2 break-words rounded-md bg-muted p-2 font-mono text-2xs text-muted-foreground">
                        {error.message}
                    </p>
                </details>
            </div>
        </div>
    );
}
